/**
 * Clarity Context Utility
 *
 * Sets custom tags in Microsoft Clarity to provide context about
 * the currently selected node and active contexts when users submit bug reports.
 */

/**
 * Sets Clarity custom tags with the current node context.
 * @param {string} nodeId - The ID of the selected node
 * @param {object} nodeData - The node data object containing name, nodeType, region, etc.
 */
export const setClarityContext = (nodeId, nodeData) => {
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') {
    console.warn('Clarity is not available');
    return;
  }

  try {
    window.clarity('set', 'nodeId', nodeId || 'Unknown');
    window.clarity('set', 'nodeName', nodeData?.name || 'Unknown');
    window.clarity('set', 'nodeType', nodeData?.nodeType || 'Unknown');

    if (nodeData?.region) {
      window.clarity('set', 'nodeRegion', nodeData.region);
    }
  } catch (error) {
    console.warn('Failed to set Clarity context:', error);
  }
};

/**
 * Sets Clarity custom tags with the active context IDs (search roots).
 * @param {object} contextIds - Object containing context IDs as keys (e.g., { 'person-123': 'person-123' })
 * @param {object} contexts - Object containing context details with name/label info
 */
export const setClarityContextIds = (contextIds, contexts) => {
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') {
    return;
  }

  try {
    const ids = contextIds ? Object.keys(contextIds) : [];
    const contextCount = ids.length;

    window.clarity('set', 'contextCount', String(contextCount));

    if (contextCount > 0) {
      // Set comma-separated list of context IDs (Clarity has value length limits)
      const contextIdList = ids.slice(0, 10).join(','); // Limit to 10 to avoid exceeding Clarity limits
      window.clarity('set', 'contextIds', contextIdList);

      // Set context names if available
      const contextNames = ids
        .slice(0, 10)
        .map((id) => {
          const context = contexts?.[id]?.value;
          return context?.name || context?.label || id;
        })
        .join(',');
      window.clarity('set', 'contextNames', contextNames);
    } else {
      window.clarity('set', 'contextIds', '');
      window.clarity('set', 'contextNames', '');
    }
  } catch (error) {
    console.warn('Failed to set Clarity context IDs:', error);
  }
};

/**
 * Clears the Clarity custom tags when no node is selected.
 */
export const clearClarityContext = () => {
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') {
    return;
  }

  try {
    window.clarity('set', 'nodeId', '');
    window.clarity('set', 'nodeName', '');
    window.clarity('set', 'nodeType', '');
    window.clarity('set', 'nodeRegion', '');
  } catch (error) {
    console.warn('Failed to clear Clarity context:', error);
  }
};

/**
 * Clears the Clarity context IDs tags.
 */
export const clearClarityContextIds = () => {
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') {
    return;
  }

  try {
    window.clarity('set', 'contextCount', '0');
    window.clarity('set', 'contextIds', '');
    window.clarity('set', 'contextNames', '');
  } catch (error) {
    console.warn('Failed to clear Clarity context IDs:', error);
  }
};
